export enum AdviceTypeEnum {
  basic = 'basic',
  extensive = 'extensive',
}

export type AdviceType = keyof typeof AdviceTypeEnum;

export const ClientTypeEnum = {
  private: 'Particulier',
  corporation: 'Belgisch bedrijf',
  intraEUCorporation: 'Bedrijf binnen EU',
  extraEUCorporation: 'Bedrijf buiten EU',
} as const;

export enum SocialInsuranceFundEnum {
  Acerta = 'Acerta',
  Xerius = 'Xerius',
  Liantis = 'Liantis',
  Partena = 'Partena',
  Securex = 'Securex',
  other = 'Geen van de bovenstaande',
}
export type SocialInsuranceFund = keyof typeof SocialInsuranceFundEnum;

export enum SocialSecretariatEnum {
  Acerta = 'Acerta',
  SDWorx = 'SD Worx',
  Liantis = 'Liantis',
  Partena = 'Partena',
  notConnected = 'Niet aangesloten',
  other = 'Aangesloten bij een ander sociaal secretariaat',
}
export type SocialSecretariat = keyof typeof SocialSecretariatEnum;

export const currentAccountancy = {
  gettingStarted: 'Ik ben net gestart/aan het starten',
  accountant: 'Met een boekhouder',
  app: 'Met een app (Accountable, Dexxter,...)',
  self: 'Zelf',
};

export type ClientType = keyof typeof ClientTypeEnum;

export enum CompanyTypesEnum {
  bv = 'Besloten Vennootschap',
  vof = 'VOF',
  commV = 'Commanditaire Vennootschap',
  emzPrimary = 'Eenmanszaak in hoofdberoep',
  emzSecondary = 'Eenmanszaak in bijberoep',
  emzStudent = 'Student-zelfstandige',
}

export enum IntakeCompanyTypesEnum {
  bv = 'Besloten Vennootschap',
  vof = 'VOF',
  commV = 'Commanditaire Vennootschap',
}

export type CompanyTypesKeys = keyof typeof CompanyTypesEnum;

export enum VatRegimeEnum {
  standard = 'standard',
  exempted = 'exempted',
  mixed = 'mixed',
  exemptedSmallCompany = 'exemptedSmallCompany',
  unknown = 'unknown',
}

export type VatRegimeKeys = keyof typeof VatRegimeEnum;

const vatRegimeLookup = {
  [VatRegimeEnum.standard]: {
    label: 'Standaardregime (gewone btw-plichtige)',
    value: VatRegimeEnum.standard,
  },
  [VatRegimeEnum.exempted]: {
    label: 'Vrijgesteld artikel 44 (bijvoorbeeld, artsen, tandartsen, kinesitherapeuten)',
    value: VatRegimeEnum.exempted,
  },
  [VatRegimeEnum.mixed]: {
    label: 'Gemengd btw plichtig',
    value: VatRegimeEnum.mixed,
  },
  [VatRegimeEnum.exemptedSmallCompany]: {
    label: 'Vrijstellingsregeling kleine onderneming',
    value: VatRegimeEnum.exemptedSmallCompany,
  },
  [VatRegimeEnum.unknown]: {
    label: 'Ik weet het niet',
    value: VatRegimeEnum.unknown,
  },
};

export const getVatRegimeValue = (vatRegime?: VatRegimeKeys) => {
  switch (vatRegime) {
    case VatRegimeEnum.standard:
    case VatRegimeEnum.exempted:
    case VatRegimeEnum.mixed:
    case VatRegimeEnum.exemptedSmallCompany:
    case VatRegimeEnum.unknown:
      return vatRegimeLookup[vatRegime].value;
    default:
      return vatRegimeLookup[VatRegimeEnum.unknown].value;
  }
};

export enum MaritalStatusEnum {
  single = 'single',
  married = 'married',
  factuallySeparated = 'factuallySeparated',
  widowed = 'widowed',
  // legallySeparated = "legallySeparated",
  legallyCoHabiting = 'legallyCoHabiting',
  // liveTogether = "liveTogether",
}

export type MaritalStatus = keyof typeof MaritalStatusEnum;

export const maritalStatusLookup = {
  [MaritalStatusEnum.single]: {
    label: 'Ongehuwd',
    value: MaritalStatusEnum.single,
  },
  [MaritalStatusEnum.married]: {
    label: 'Gehuwd',
    value: MaritalStatusEnum.married,
  },
  [MaritalStatusEnum.legallyCoHabiting]: {
    label: 'Wettelijk samenwonend',
    value: MaritalStatusEnum.legallyCoHabiting,
  },
  [MaritalStatusEnum.factuallySeparated]: {
    label: 'Feitelijk gescheiden',
    value: MaritalStatusEnum.factuallySeparated,
  },
  [MaritalStatusEnum.widowed]: {
    label: 'Weduwe/weduwnaar',
    value: MaritalStatusEnum.widowed,
  },
  // [MaritalStatusEnum.legallySeparated]: {
  //   label: "Feitelijk gescheiden",
  //   value: MaritalStatusEnum.legallySeparated,
  // },

  // [MaritalStatusEnum.liveTogether]: {
  //   label: "Samenwonend",
  //   value: MaritalStatusEnum.liveTogether,
  // },
};

export enum CallFrequencyEnum {
  yearly = '1 keer per jaar',
  quarterly = '1 keer per kwartaal',
  monthly = '1 keer per maand',
}
export type FrequencyKeys = keyof typeof CallFrequencyEnum;

export enum DocumentTypeEnum {
  purchase = 'aankoop',
  sales = 'verkoop',
}

export type DocumentType = keyof typeof DocumentTypeEnum;

export enum VatEnum {
  noBtw = 'noBtw',
  six = 'six',
  twelve = 'twelve',
  twentyOne = 'twentyOne',
  ICD = 'ICD',
  ICL = 'ICL',
  EX = 'EX',
  KO = 'KO',
  MC = 'MC',
}

export type VatOptionsKeys = keyof typeof VatEnum;

// Contracts
export enum EversignWebhookEventTypesEnum {
  document_created,
  document_viewed,
  document_sent,
  document_signed,
  document_declined,
  document_forwarded,
  document_trashed,
  signer_removed,
  signer_bounced,
  document_completed,
  document_expired,
  document_revoked,
  document_cancelled,
  email_validation_waived,
}

export type EversignWebhookEventType = keyof typeof EversignWebhookEventTypesEnum;

export enum EversignFieldTypesEnum {
  signature = 'signature',
  initials = 'initials',
  dateSigned = 'date_signed',
  note = 'note',
  text = 'text',
  checkbox = 'checkbox',
  dropdown = 'dropdown',
  attachment = 'attachment',
}

export type EversignFieldTypes = keyof typeof EversignFieldTypesEnum;

export enum ContractStatusEnum {
  contractRequested = 'contractRequest', // When user makes initial request
  approved = 'approved', // Offer has been approved by expert and made available for user to sign it
  eSignatureRequested = 'eSignatureRequested',
  rejected = 'rejected', // At any point when expert rejects offer
  cancelled = 'cancelled', // If user cancels the offer request/process
  signed = 'signed', // User signed offer
}

export type ContractStatus = keyof typeof ContractStatusEnum;

export enum ContractTypeEnum {
  offer = 'offer',
  proxy = 'proxy',
  other = 'other',
}

export type ContractType = keyof typeof ContractTypeEnum;

export enum InvoiceTypeEnum {
  invoice = 'Factuur',
  creditNote = 'Creditnota',
}
export type InvoiceType = keyof typeof InvoiceTypeEnum;

export enum MonthNames {
  january = 'Januari',
  february = 'Februari',
  march = 'Maart',
  april = 'April',
  may = 'Mei',
  june = 'Juni',
  july = 'Juli',
  august = 'Augustus',
  september = 'September',
  october = 'Oktober',
  november = 'November',
  december = 'December',
}
